import { StatusBar } from 'expo-status-bar';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { NotificationPermissionIllustration } from '@/components/atoms/illustrations/NotificationPermissionIllustration';
import { PrimaryButton } from '@/components/atoms/button';
import { useTranslation } from '@/i18n/useTranslation';
import { colorPrimary, colorSemantic, radii, spacing } from '@/theme';

interface NotificationPermissionPromptProps {
  readonly onEnable: () => void;
  readonly onSkip: () => void;
}

const NotificationPermissionPrompt = ({ onEnable, onSkip }: NotificationPermissionPromptProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  return (
    <Modal animationType="fade" transparent={false} visible onRequestClose={onSkip}>
      <View style={styles.safeArea}>
        <StatusBar style="light" />
        <ScrollView
          bounces={false}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: insets.top, paddingBottom: insets.bottom },
          ]}
          testID="notification-prompt-scroll">
          <View style={styles.illustrationArea} testID="notification-prompt-illustration-area">
            <NotificationPermissionIllustration />
          </View>
          <View style={styles.content}>
            <View style={styles.textBlock}>
              <Text style={styles.title}>{t('notificationPermissionPrompt.title')}</Text>
              <Text style={styles.body}>{t('notificationPermissionPrompt.body')}</Text>
            </View>
            <View style={styles.buttonGroup} testID="notification-prompt-buttons">
              <PrimaryButton onPress={onEnable} testID="notification-prompt-enable">
                {t('notificationPermissionPrompt.enableButton')}
              </PrimaryButton>
              <Pressable
                accessibilityRole="button"
                style={styles.skipButton}
                testID="notification-prompt-skip"
                onPress={onSkip}>
                <Text style={styles.skipText}>{t('notificationPermissionPrompt.skipButton')}</Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colorPrimary.nd500,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  illustrationArea: {
    width: '100%',
  },
  content: {
    width: '100%',
    alignItems: 'center',
    gap: spacing[4],
    paddingTop: spacing[4],
  },
  textBlock: {
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: spacing[4],
  },
  title: {
    textAlign: 'center',
    fontSize: 22,
    lineHeight: 30,
    fontWeight: '700',
    color: colorSemantic.colorTextNlPrimary,
  },
  body: {
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 20,
    color: colorSemantic.colorTextNlSecondary,
  },
  buttonGroup: {
    width: '100%',
    gap: spacing[4],
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
  },
  skipButton: {
    height: 40,
    borderWidth: 1,
    borderColor: colorSemantic.colorBorderNlWeak,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  skipText: {
    color: colorSemantic.colorTextNlPrimary,
    fontSize: 14,
    lineHeight: 20,
  },
});

export { NotificationPermissionPrompt };
